import type { Paddle, PricePreviewItem } from '@paddle/paddle-js'
import { useTranslation } from 'next-i18next/pages'
import { useEffect, useState } from 'react'

import {
  type SubscriptionPlan,
  type SubscriptionPlanConfig,
  type SubscriptionPlanSchema,
  subscriptionPlanSchema,
} from '../../types/subscription'
import { useMemoryCache } from '../../utils/cache'
import {
  getSubscriptionPlanSchema,
  initPaddle,
  isPaddleId,
  type PaddleCredentials,
} from '../../utils/paddle'

type Props = {
  cacheKey?: string
  discountId?: string
  paddleCredentials: PaddleCredentials
  planConfigs: SubscriptionPlanConfig[]
}

export const usePaddle = ({
  cacheKey,
  discountId,
  paddleCredentials,
  planConfigs,
}: Props) => {
  const { i18n } = useTranslation()
  const [paddle, setPaddle] = useState<Paddle | undefined>()

  // Cache paddle plan lists. When cache key is not defined, plans to cache is not stored.
  const paddleCache = useMemoryCache<SubscriptionPlan[]>(
    cacheKey || 'default-paddle-cache'
  )
  const [plans, setPlans] = useState<SubscriptionPlan[]>(paddleCache.data || [])

  // Download and initialize Paddle instance
  useEffect(() => {
    let paddleInstance: Paddle | undefined

    const initialize = async () => {
      try {
        paddleInstance = await initPaddle({
          credentials: paddleCredentials,
          locale: i18n.language,
        })
        if (paddleInstance) {
          setPaddle(paddleInstance)
        }
      } catch (error) {
        console.error(error)
      }
    }

    void initialize()

    return () => {
      paddleInstance?.Checkout.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load localized prices from Paddle
  useEffect(() => {
    if (!paddle || !planConfigs.length) {
      return
    }

    const fetchPrices = async () => {
      const priceItems = planConfigs.reduce<PricePreviewItem[]>(
        (items, planConfig) => {
          if (isPaddleId(planConfig.paddlePriceId)) {
            items.push({
              quantity: 1,
              priceId: planConfig.paddlePriceId!,
            })
          }
          return items
        },
        []
      )

      try {
        const response = await paddle.PricePreview({
          items: priceItems,
          discountId,
        })
        const planList = planConfigs.reduce<SubscriptionPlan[]>(
          (acc, subscriptionPlanConfig) => {
            const item = response.data.details.lineItems.find(
              (item) => item.price.id === subscriptionPlanConfig.paddlePriceId
            )

            if (item) {
              const plan = getSubscriptionPlanSchema(response, item)
              const parse = subscriptionPlanSchema.safeParse(plan)
              if (parse.success) {
                acc.push({
                  ...(plan as SubscriptionPlanSchema),
                  config: subscriptionPlanConfig,
                })
              } else {
                console.error('Invalid Paddle plan data', plan, parse.error)
              }
            }

            return acc
          },
          []
        )
        setPlans(planList)

        // Store plans to cache only if cache key is defined.
        if (cacheKey) {
          paddleCache.set(planList)
        }
      } catch (error) {
        console.error(error)
      }
    }

    void fetchPrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountId, paddle, planConfigs])

  return {
    paddle,
    plans,
  }
}
